import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { WhatsAppIcon } from "@/components/Icons";
import { BRAND, NAV_LINKS } from "@/data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-zinc-200 py-3" : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2.5 sm:gap-3 group shrink-0" data-testid="brand-logo">
          <img src="/logo-transparent.png" alt="Sahi Claim logo" className="h-12 w-12 sm:h-[60px] sm:w-[60px] object-contain" />
          <span className="text-lg sm:text-2xl font-bold tracking-tight text-ink whitespace-nowrap">Sahi <span className="text-forest">Claim</span><sup className="text-[0.5em] align-super text-ink font-semibold ml-0.5">™</sup></span>
        </a>

        <nav className="hidden md:flex items-center gap-8 lg:gap-9 flex-nowrap ml-12 lg:ml-16">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-zinc-700 hover:text-forest transition-colors whitespace-nowrap">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" data-testid="nav-whatsapp" aria-label="Chat on WhatsApp"
            className="h-11 w-11 rounded-full border border-zinc-300 flex items-center justify-center text-forest hover:bg-forest hover:text-white hover:border-forest transition-colors">
            <WhatsAppIcon size={20} />
          </a>
          <a href={BRAND.phoneHref} data-testid="nav-cta" className="btn-primary !px-6 !py-3 text-sm whitespace-nowrap">
            <Phone size={16} /> Free Claim Review
          </a>
        </div>

        {/* Mobile: keep a compact CTA visible + hamburger */}
        <div className="md:hidden flex items-center gap-1.5 shrink-0">
          <a href={BRAND.phoneHref} data-testid="mobile-cta" className="btn-primary !px-3 !py-2.5 text-xs whitespace-nowrap">
            <Phone size={14} /> Free Review
          </a>
          <button className="text-ink p-1 shrink-0" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
          className="md:hidden bg-white border-t border-zinc-200 mt-3 overflow-hidden" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)}
                className="text-base font-medium text-zinc-800">{l.label}</a>
            ))}
            <div className="flex gap-3 mt-2">
              <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="btn-secondary flex-1 !py-3 text-sm">
                <WhatsAppIcon size={16} /> WhatsApp
              </a>
              <a href={BRAND.phoneHref} onClick={() => setOpen(false)} className="btn-primary flex-1 !py-3 text-sm">
                <Phone size={16} /> Call
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Navbar;
